import React, { useCallback, useEffect, useRef } from "react"
import Box from "@mui/material/Box"
import { loadSshSettings } from "./TerminalContext"
import { scrollbarSx } from "../../shared/scrollbar"
import { useTerminalOptions } from "../appearance/AppearanceContext"

// Strip ANSI escape sequences so pattern matching works on clean text.
function stripAnsi(s) {
  return s.replace(/\x1b(?:[@-Z\\-_]|\[[0-?]*[ -/]*[@-~]|\].*?(?:\x07|\x1b\\))/g, "")
}

// Patterns in the last visible lines that indicate the CLI is waiting for
// input or has finished work and returned to an interactive prompt.
const ATTENTION_RE = /(?:^\s*\?[ \t]|^\s*>\s*$|\[y\/n]|\(y\/n\)|\(Y\/n\)|\(y\/N\)|press\s+enter|press\s+any\s+key)/im

// ── Clipboard write helper ─────────────────────────────────────────────────────
// On HTTPS use the Async Clipboard API. On HTTP fall back to execCommand.
//
// IMPORTANT: _legacyCopy must NOT call focus() on any element.
// The old textarea-focus approach blurred xterm, which caused xterm to clear its
// internal selection model. After the blur+refocus cycle, term.hasSelection()
// returned false, so the Ctrl+C handler silently fell through to SIGINT instead
// of copying. Fix: use a hidden <div> + DOM Range/Selection so the clipboard
// write never disturbs xterm's focus or selection state.
function _legacyCopy(text) {
  const div = document.createElement("div")
  div.appendChild(document.createTextNode(text))
  div.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0;pointer-events:none;white-space:pre"
  document.body.appendChild(div)
  const range = document.createRange()
  range.selectNodeContents(div)
  const sel = window.getSelection()
  sel.removeAllRanges()
  sel.addRange(range)
  try { document.execCommand("copy") } catch {}
  sel.removeAllRanges()
  document.body.removeChild(div)
}

function clipboardWrite(text) {
  if (!text) return
  if (window.isSecureContext && navigator.clipboard) {
    navigator.clipboard.writeText(text).catch(() => _legacyCopy(text))
    return
  }
  _legacyCopy(text)
}

// ── TerminalSession ────────────────────────────────────────────────────────────

export default function TerminalSession({
  active, id, onStatusChange, onFitReady, onFocusReady, onAttentionChange,
  reconnectToggle, startupCommand, sessionId, onSessionId, minimized, profileOverride,
  projectDirOverride, paneStyle,
}) {
  const termDivRef        = useRef(null)
  const xtermRef          = useRef(null)
  const fitRef            = useRef(null)
  const wsRef             = useRef(null)
  const initialized       = useRef(false)
  const dataDisp          = useRef(null)
  const resizeDisp        = useRef(null)
  const receivedBuf       = useRef("")
  const attentionTimer    = useRef(null)
  const resizeObs         = useRef(null)
  const fitTimer          = useRef(null)

  // Theme tokens drive xterm's colors/font. The ref feeds the initial construction
  // (initXterm runs once); the effect re-applies every later theme change live.
  const termOptions = useTerminalOptions()
  const termOptionsRef = useRef(termOptions)
  termOptionsRef.current = termOptions
  useEffect(() => {
    const term = xtermRef.current
    if (!term) return
    Object.entries(termOptions).forEach(([key, value]) => { term.options[key] = value })
    try { fitRef.current?.fit() } catch {}
  }, [termOptions])

  const onStatusChangeRef = useRef(onStatusChange)
  useEffect(() => { onStatusChangeRef.current = onStatusChange }, [onStatusChange])

  const onFitReadyRef = useRef(onFitReady)
  useEffect(() => { onFitReadyRef.current = onFitReady }, [onFitReady])

  const onFocusReadyRef = useRef(onFocusReady)
  useEffect(() => { onFocusReadyRef.current = onFocusReady }, [onFocusReady])

  const onAttentionChangeRef = useRef(onAttentionChange)
  useEffect(() => { onAttentionChangeRef.current = onAttentionChange }, [onAttentionChange])


  const stableAttention = useCallback((v) => {
    onAttentionChangeRef.current?.(v)
  }, [])

  const onSessionIdRef = useRef(onSessionId)
  useEffect(() => { onSessionIdRef.current = onSessionId }, [onSessionId])

  const startupCommandRef = useRef(startupCommand)
  useEffect(() => { startupCommandRef.current = startupCommand }, [startupCommand])

  const sessionIdRef = useRef(sessionId)
  useEffect(() => { sessionIdRef.current = sessionId }, [sessionId])

  const activeRef = useRef(active)
  useEffect(() => { activeRef.current = active }, [active])

  const stableStatusChange = useCallback((status, msg) => {
    onStatusChangeRef.current(status, msg)
  }, [])

  const stableFitReady = useCallback((fn) => {
    onFitReadyRef.current(fn)
  }, [])

  const stableFocusReady = useCallback((fn) => {
    onFocusReadyRef.current(fn)
  }, [])

  const profileOverrideRef = useRef(profileOverride)
  useEffect(() => { profileOverrideRef.current = profileOverride }, [profileOverride])

  const projectDirOverrideRef = useRef(projectDirOverride ?? null)
  useEffect(() => { projectDirOverrideRef.current = projectDirOverride ?? null }, [projectDirOverride])

  const connect = useCallback(() => {
    if (wsRef.current) { try { wsRef.current.close() } catch {} }
    const cfg = loadSshSettings()
    const override = profileOverrideRef.current
    const username = override?.username ?? cfg.username
    const password = override?.password ?? cfg.password
    if (!username || !cfg.host) {
      stableStatusChange("error", "SSH not configured — open Settings")
      return
    }
    if (!xtermRef.current) return

    stableAttention(false)
    clearTimeout(attentionTimer.current)
    receivedBuf.current = ""
    stableStatusChange("connecting")
    xtermRef.current.clear()
    xtermRef.current.writeln("\x1b[33mConnecting via SSH…\x1b[0m")

    dataDisp.current?.dispose()
    resizeDisp.current?.dispose()
    dataDisp.current  = null
    resizeDisp.current = null

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:"
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws/terminal/`)
    wsRef.current = ws
    ws.binaryType = "arraybuffer"

    ws.onopen = () => {
      const { cols, rows } = xtermRef.current
      ws.send(JSON.stringify({
        t: "connect",
        host:       cfg.host,
        port:       cfg.port,
        username:   username,
        password:   password,
        projectDir: projectDirOverrideRef.current ?? cfg.projectDir,
        command:    startupCommandRef.current ?? "",
        session_id: sessionIdRef.current ?? "",
        cols,
        rows,
      }))
    }

    ws.onmessage = (e) => {
      if (e.data instanceof ArrayBuffer) {
        const bytes = new Uint8Array(e.data)
        xtermRef.current?.write(bytes)

        // Attention detection: bell = immediate; prompt patterns = after 1.5 s idle
        const text = new TextDecoder("utf-8", { fatal: false }).decode(bytes)
        receivedBuf.current = (receivedBuf.current + text).slice(-2000)
        if (text.includes("\x07")) {
          stableAttention(true)
        } else {
          clearTimeout(attentionTimer.current)
          attentionTimer.current = setTimeout(() => {
            const clean = stripAnsi(receivedBuf.current)
            const lastLines = clean.split("\n").filter(l => l.trim()).slice(-8).join("\n")
            if (ATTENTION_RE.test(lastLines)) stableAttention(true)
          }, 1500)
        }
      } else {
        try {
          const msg = JSON.parse(e.data)
          if (msg.t === "connected") {
            stableStatusChange("connected")
            if (msg.session_id) onSessionIdRef.current(msg.session_id)
            // Force size sync after attach (tmux may need it)
            if (xtermRef.current) {
              const { cols, rows } = xtermRef.current
              ws.send(JSON.stringify({ t: "resize", cols, rows }))
            }
          } else if (msg.t === "error") {
            stableStatusChange("error", msg.msg)
            xtermRef.current?.writeln(`\r\n\x1b[31mError: ${msg.msg}\x1b[0m`)
          } else if (msg.t === "closed") {
            stableStatusChange("idle")
            xtermRef.current?.writeln("\r\n\x1b[33m[Session closed]\x1b[0m")
          }
        } catch {}
      }
    }

    let wsErrored = false
    ws.onerror = () => {
      wsErrored = true
      stableStatusChange("error", "WebSocket error")
      xtermRef.current?.writeln("\r\n\x1b[31m[WebSocket error]\x1b[0m")
    }
    ws.onclose = () => { if (!wsErrored) stableStatusChange("idle") }

    dataDisp.current = xtermRef.current.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(new TextEncoder().encode(data).buffer)
        // User responded — clear attention and reset the received buffer
        stableAttention(false)
        clearTimeout(attentionTimer.current)
        receivedBuf.current = ""
      }
    })

    resizeDisp.current = xtermRef.current.onResize(({ cols, rows }) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ t: "resize", cols, rows }))
      }
    })
  }, [stableStatusChange])

  const initXterm = useCallback(async () => {
    if (initialized.current || !termDivRef.current) return
    initialized.current = true

    const [{ Terminal }, { FitAddon }, { Unicode11Addon }] = await Promise.all([
      import("@xterm/xterm"),
      import("@xterm/addon-fit"),
      import("@xterm/addon-unicode11"),
    ])
    await import("@xterm/xterm/css/xterm.css")

    const term = new Terminal({
      ...termOptionsRef.current,
      scrollback: 5000,
      allowProposedApi: true,
    })

    // Ctrl+C: copy if selection, else pass SIGINT through to PTY.
    // Ctrl+V: return false — xterm skips \x16 without calling preventDefault, so the
    //   browser fires its native paste event into xterm's textarea (xterm handles it).
    term.attachCustomKeyEventHandler((e) => {
      if (e.type !== "keydown") return true
      // Shift+Enter: insert a newline instead of submitting. xterm ignores the
      // Shift modifier on Enter and would send a bare \r (which submits), so we
      // send ESC+CR — the convention Ink-based TUIs (e.g. claude CLI) and
      // readline interpret as a line break in the input.
      if (e.code === "Enter" && e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault()
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(new TextEncoder().encode("\x1b\r").buffer)
        }
        return false
      }
      if (e.ctrlKey && e.code === "KeyC" && term.hasSelection()) {
        // preventDefault stops the browser's native copy from overwriting ours
        // after xterm returns early (before its own preventDefault runs).
        e.preventDefault()
        clipboardWrite(term.getSelection())
        return false
      }
      if (e.ctrlKey && e.code === "KeyV") return false
      return true
    })

    // OSC 52 handler: when tmux has set-clipboard on, it sends an OSC 52 escape sequence
    // whenever text is copied to its paste buffer (e.g. via mouse selection in mouse mode).
    // xterm.js forwards this to our handler; we decode it and write to the browser clipboard.
    // This is the only reliable copy path when a TUI like claude CLI enables tmux mouse tracking.
    term.parser.registerOscHandler(52, (data) => {
      const semi = data.indexOf(";")
      if (semi === -1) return false
      const b64 = data.slice(semi + 1)
      if (!b64 || b64 === "?") return false
      try {
        clipboardWrite(atob(b64))
        return true
      } catch {
        return false
      }
    })

    // xterm's default width tables are Unicode 6 — emoji are width-1 cells there,
    // but tmux (utf8proc) and Claude Code treat them as width 2. Every emoji in a
    // statusline (🤖, 🚀, …) then shifts xterm's cursor one column left of where
    // tmux believes it is, so tmux's in-place row redraws land misaligned and leave
    // ghost glyphs — while native clients (width-2, agreeing with tmux) render fine.
    // Unicode 11 tables make xterm agree with the host.
    term.loadAddon(new Unicode11Addon())
    term.unicode.activeVersion = "11"

    const fit = new FitAddon()
    term.loadAddon(fit)
    term.open(termDivRef.current)
    fit.fit()

    // Keep xterm's column/row count locked to the visible pane at all times.
    // Without this, anything that changes the pane size *without* a fit() — the
    // split divider drag, a window resize while split, browser zoom — leaves
    // xterm's cols out of sync with the host tmux pane. Claude Code redraws its
    // statusline in place (cursor-up + erase-line), so a column mismatch makes
    // that redraw land on the wrong row and ghost ("55h 123 …"). fit() fires
    // onResize, which pushes the corrected size down to the PTY/tmux.
    const debouncedFit = () => {
      clearTimeout(fitTimer.current)
      fitTimer.current = setTimeout(() => {
        const el = termDivRef.current
        if (!el || el.clientWidth === 0 || el.clientHeight === 0) return
        try { fitRef.current?.fit() } catch {}
      }, 80)
    }
    resizeObs.current = new ResizeObserver(debouncedFit)
    resizeObs.current.observe(termDivRef.current)

    // Select-to-copy: mouseup fires after xterm's pointerup (which finalizes the selection
    // model), and is always within browser user-activation context → execCommand works on HTTP.
    // onSelectionChange fires from xterm's rAF render cycle — outside user-activation — so
    // execCommand silently fails there. Guard with activeRef so inactive tabs don't clobber clipboard.
    const handleMouseUp = () => {
      if (!activeRef.current) return
      const sel = term.getSelection()
      if (sel) clipboardWrite(sel)
    }
    term.element.addEventListener("mouseup", handleMouseUp)

    // Right-click: copy if selection; otherwise let browser show native paste menu.
    // Must be attached after term.open() — term.element is undefined before that.
    const handleContextMenu = (e) => {
      if (term.hasSelection()) {
        e.preventDefault()
        clipboardWrite(term.getSelection())
      }
    }
    term.element.addEventListener("contextmenu", handleContextMenu)

    xtermRef.current = term
    fitRef.current   = fit

    stableFitReady(() => { fitRef.current?.fit() })
    stableFocusReady(() => { xtermRef.current?.focus() })

    setTimeout(connect, 150)

    return () => {
      term.element?.removeEventListener("mouseup", handleMouseUp)
      term.element?.removeEventListener("contextmenu", handleContextMenu)
    }
  }, [connect, stableFitReady])

  useEffect(() => {
    let cleanup
    initXterm().then((fn) => { cleanup = fn })
    return () => {
      cleanup?.()
      dataDisp.current?.dispose()
      resizeDisp.current?.dispose()
      try { resizeObs.current?.disconnect() } catch {}
      clearTimeout(fitTimer.current)
      try { wsRef.current?.close() } catch {}
      try { xtermRef.current?.dispose() } catch {}
    }
  }, [initXterm])

  // Refit and focus when becoming active tab
  useEffect(() => {
    if (active) {
      setTimeout(() => fitRef.current?.fit(), 60)
      setTimeout(() => xtermRef.current?.focus(), 80)
    }
  }, [active])

  // Refit and focus when restored from minimize
  useEffect(() => {
    if (!minimized && active) {
      setTimeout(() => fitRef.current?.fit(), 100)
      setTimeout(() => xtermRef.current?.focus(), 120)
    }
  }, [minimized, active])

  useEffect(() => {
    if (reconnectToggle > 0) connect()
  }, [reconnectToggle, connect])

  return (
    <Box
      sx={{
        flex: 1,
        ...(paneStyle || {}),
        display: active ? "flex" : "none",
        flexDirection: "column",
        overflow: "hidden",
        minWidth: 0,
        "& .xterm": { height: "100%", padding: "4px 6px" },
        "& .xterm-viewport": {
          overflowY: "scroll",
          ...scrollbarSx,
        },
      }}
    >
      <Box ref={termDivRef} sx={{ flex: 1, overflow: "hidden" }} />
    </Box>
  )
}
